/* **A box that holds a photo's place** — the one treatment every network image in this app wears.
 *
 * A stylesheet with no component, like `emberButton.module.css` and `MenuSurface.module.css` beside
 * it. A component would have to take the geometry as props — three avatar sizes, a cover height, a
 * radius each — which is a prop bag, and it would put a React boundary around one `<img>`. So the
 * SURFACE declares the geometry and this file declares the three facts that are the same wherever a
 * photo is drawn:
 *
 *   the fit          a photo whose aspect ratio differs from its box is cropped, never squashed
 *   the ground       a neutral token fill, visible until the file decodes and forever if it fails
 *   the paint box    (`.painted` only) where a background-painted photo is positioned and scaled
 *
 * **Nothing is declared on both sides.** A declaration living in both a shared module and a page's
 * own is settled by CHUNK ORDER, which differs between the dev server and the build — that cost a
 * focus ring that was neutral in development and accent in the shipped bundle, and the argument is
 * written out at length in `emberButton.module.css`. So a surface using these classes declares its
 * `width`, `height`, `border`, `border-radius` and layout, and none of `object-fit`, `background`,
 * `background-position` or `background-size`.
 *
 * ── The reservation is the SURFACE's job, and it is the whole point ──────────────────────────
 * This file cannot reserve space, because the size is what varies. What the rule requires of every
 * caller is that the box has a width and a height (or an aspect ratio) in CSS, so the slot exists
 * before the fetch does. `shared/AGENTS.md`, "A photo holds its own box", states it and records what
 * was measured; `pages/client-detail/AGENTS.md` records the numbers for the two the owner named.
 *
 * ── No shimmer ──────────────────────────────────────────────────────────────────────────────
 * The ground is a flat token fill and nothing about it moves. A pulsing skeleton is motion with
 * nothing behind it, and the owner cuts those consistently. If a fade were ever wanted when the
 * image lands it would go through `shared/design-system/motion` with `reducedMotion` passed in,
 * never a keyframe in a stylesheet — `tokens/base.css`'s reduced-motion block does not reach a Web
 * Animations API animation, which is the trap that rule exists for.
 *
 * ── The ground is `--bg-sunken`, one value, deliberately ─────────────────────────────────────
 * Not a custom property with a per-surface override: a `--photo-ground: …` at a call site would
 * escape the Stylelint value allowlist entirely (it is keyed on REAL property names, so
 * `--photo-ground: #eee` passes the build where `background: #eee` fails), and two grounds would be
 * two answers to a question nobody has asked twice. `--bg-sunken` is the recessed surface in both
 * themes and it is what every photo in the clients module already stood on.
 *
 * ── An `<img>` that fails does NOT need a fallback element ──────────────────────────────────
 * With a width, a height and `alt=""`, a request that 404s or is blocked leaves Chrome drawing
 * nothing at all — no broken-file glyph — so the ground below it is the error state. Verified by
 * aborting the request and reading the box back: 26x26 in every frame, ground showing. That is why
 * every decorative image here carries `alt=""` rather than a description; a photo of a client whose
 * name is already in the next element has nothing to add. */

/* An `<img>`. The surface gives it a width, a height and its `display` — the last of those because
 * the three avatars in the clients module already disagree (`grid` on the detail's, which shares its
 * box with the monogram; `block` on the preview panel's; the flex default on the roster row's), and
 * a `display` here would be a declaration on both sides, settled by chunk order. */
.photo {
  object-fit: cover;
  background: var(--bg-sunken);
}

/* A box that paints its photo as a BACKGROUND rather than as an `<img>` — the plan card's cover,
 * where the image is decoration behind a scrim and a heading, and an `<img>` would need a second
 * absolutely-positioned element to sit under them. The surface gives it a height.
 *
 * A background image contributes nothing to layout in any state, so this form reserves its space by
 * construction rather than by discipline. It is here so the two spellings of one rule live in one
 * file and cannot drift; it has one caller today (`shared/ui/PlanCard`) and that is stated rather
 * than hidden. */
.painted {
  background-color: var(--bg-sunken);
  background-position: center;
  background-size: cover;
}

/* **The picture, held** — worn by an `<img>` whose group has not been revealed yet
 * (`shared/lib/usePhotoReveal`). The box, its geometry and the grey ground above are untouched;
 * only the photograph is out of sight, in a hole that is already the photograph's size.
 *
 * This is the one `opacity` in this file, and the loading policy's ban on "an `opacity` hiding a
 * symptom" is about hiding MOVEMENT. Nothing here can move: `.photo`'s caller declares the width
 * and the height, so the box is at its final size in every frame of the wait, and what this
 * switches is ink inside it. The reasoning is `photoRevealGate`'s and is written out there.
 *
 * No transition on it. Going out of sight is instantaneous — the image was never shown — and
 * coming back is the hand-over, which is `skeleton.module.css`'s `.reveal` and is a real animation
 * with `prefers-reduced-motion` already answered. Two mechanisms on one property would fight. */
.holding {
  opacity: 0;
}
